import React, { useState } from 'react';
import { Package, Truck, MapPin, Clock, CheckCircle2, Phone, Mail, ArrowRight, Globe, Shield, DollarSign, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';
import QuoteRequestForm from '../components/QuoteRequestForm';

const InternationalShippingMississaugaPage = () => {
  const { toast } = useToast();

  const benefits = [
    {
      icon: MapPin,
      title: "Local Mississauga Service",
      description: "Convenient pickup and drop-off options throughout Mississauga with dedicated local support for the GTA's third-largest city."
    },
    {
      icon: DollarSign,
      title: "Competitive Rates",
      description: "Compare DHL, FedEx, UPS, and trusted carriers to secure the best rates for your international shipments from Mississauga."
    },
    {
      icon: Truck,
      title: "Reliable Carriers",
      description: "Partner with major international carriers ensuring your parcels reach India and 185+ countries safely and on schedule."
    },
    {
      icon: Globe,
      title: "Door-to-Door Delivery",
      description: "Complete service from your Mississauga address to any destination worldwide with real-time tracking included."
    },
    {
      icon: Shield,
      title: "Customs Support",
      description: "Expert assistance with customs documentation and clearance procedures for hassle-free international delivery."
    },
    {
      icon: Users,
      title: "24/7 Support",
      description: "Round-the-clock assistance for all your shipping needs, quotes, and tracking inquiries from Mississauga."
    }
  ];

  const faqs = [
    {
      question: "How much does international shipping from Mississauga cost?",
      answer: "Shipping costs depend on weight, dimensions, destination, and carrier. We compare rates from DHL, FedEx, UPS to provide competitive pricing. For relocation shipments over 100kg, rates start at $7.99/kg (India airport-to-airport, 100kg min) or $8.49/kg (doorstep, 138kg min). Contact us for an instant quote."
    },
    {
      question: "Where can I drop off my parcel in Mississauga?",
      answer: "Shippzy has partner drop-off locations throughout Mississauga for your convenience. We also offer pickup services from your home or business. Contact our team for the nearest location."
    },
    {
      question: "Can I ship to India from Mississauga?",
      answer: "Absolutely! We specialize in shipping from Mississauga to India with door-to-door delivery, customs clearance support, and competitive rates on all shipments."
    },
    {
      question: "Which carriers does Shippzy use from Mississauga?",
      answer: "We work with DHL, FedEx, UPS, and Purolator. By comparing rates, we ensure you get the best combination of price and reliability for Mississauga shipments."
    }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>International Shipping Mississauga | Affordable Courier – Shippzy</title>
        <meta name="description" content="Ship internationally from Mississauga with Shippzy. Affordable courier to India and 185+ countries. Compare DHL, FedEx, UPS rates with 24/7 support." />
        <link rel="canonical" href="https://www.shippzy.ca/international-shipping-mississauga" />
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white pt-24 pb-12">
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                📦 Serving Mississauga & GTA
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                International Shipping from Mississauga
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Shippzy offers reliable and affordable international shipping from Mississauga to India and over 185 countries worldwide. We partner with DHL, FedEx, and UPS to provide the best rates and fastest delivery. Available 24/7 for quotes, tracking, and support.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg"
                  onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Get Free Quote
                </Button>
                <Button 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg"
                  onClick={() => window.location.href = 'tel:+14376160758'}
                >
                  Call: +1 (437) 616-0758
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Mississauga Customers Choose Shippzy</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Local expertise with international reach, serving Mississauga's diverse shipping needs
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Common questions about shipping from Mississauga</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && (
                      <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related Services</h3>
              <div className="flex flex-wrap gap-4 justify-center">
                <a href="/shipping-to-india-from-canada" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Shipping to India
                </a>
                <span className="text-gray-400">•</span>
                <a href="/cheap-shipping-canada-to-india" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Cheap shipping options
                </a>
                <span className="text-gray-400">•</span>
                <a href="/international-courier-toronto" className="text-red-600 hover:text-red-700 font-semibold underline">
                  Toronto courier
                </a>
              </div>
            </div>
          </div>
        </section>

        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <QuoteRequestForm />
          </div>
        </section>

        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Trusted International Courier in Mississauga</h3>
            <p className="text-gray-300 mb-6">
              Ship internationally from Mississauga with confidence. 24/7 support available for all your shipping needs.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default InternationalShippingMississaugaPage;